// Página "Margem por setor": tarefa do Plano de ação (Ciclo 1 › Semana 1) e aba de
// Financeiro › DRE & Resultado. Lucro de cada setor mês a mês, na estrutura da DRE, no
// período escolhido (até 24 meses). A soma dos setores é igual à DRE em
// toda linha (o servidor confere e devolve a diferença; aqui ela aparece em vermelho).
// Rota: GET /financeiro/dre-setores (dre-engine). Contas puras em dadosSetores.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Collapse, Popconfirm, Segmented, Space, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePerms } from '../../../hooks/usePerms';
import { metaSetor } from '../../../utils/setoresDre';
import {
  aceitarSetorCusto, buscarDreSetores, desfazerAceiteSetorCusto, recalcularFechamento, ErroDre,
  type Agrupar, type DreSetores, type EscopoAceite, type LinhaMargem, type PropsPagina,
} from './apiDre';
import {
  avisosDosMeses, blocosDeMeses, calculadoEmMaisAntigo, chaveReceita, contagemPendencia, dataBr, itensDaPendencia, juntarBlocos,
  linhasDoGrafico, linhasMargem, margemPct, MAX_MESES_MARGEM, pctTexto, porSetorCanonico, reais, reaisCurto, rotuloItem,
  rotuloLinha, rotuloMes, rotuloMesCurto, rotuloRegra, setoresDaResposta, tendenciaTexto, totalMaoObraParada, totalSemSetor,
  valoresAcumulados, type MesSerie, type ModoMargem,
} from './dadosSetores';
import { nomesSetores, resumoVias, somarVias } from './dadosSetoresCusto';
import {
  AcessoNaoConferido, Carregando, COR_TENDENCIA, EtiquetaSetor, FiltroDePeriodo, FiltroDeSetor, FiltroDeTendencia, IrPara,
  LINK_AREAS, LINK_LANCAMENTOS, LINK_MAO_OBRA, SemAcesso, TooltipEmpilhado, TRACO_TENDENCIA, usePeriodo,
} from './comumDre';

const { Text } = Typography;
const PERM = 'financeiro.dre.ver';
const PERM_REVISAR = 'financeiro.setor_custo.revisar';   // aceitar/desfazer "segue o contrato"
const LINHAS_DE_LUCRO: { value: LinhaMargem; label: string }[] = [
  { value: 'lucro_bruto', label: 'Lucro bruto' },
  { value: 'resultado_op', label: 'Resultado operacional' },
  { value: 'lucro_liq', label: 'Lucro líquido' },
  { value: 'lucro_final', label: 'Lucro final' },
];

const PENDENCIAS: { chave: string; titulo: string; onde: React.ReactNode }[] = [
  {
    chave: 'parcelas_sem_setor', titulo: 'Parcelas sem setor',
    onde: <span>Corrija em <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara>, ou na parcela (Financeiro › Recebimentos › Contas a Receber › Detalhes › Rateio por setor).</span>,
  },
  {
    chave: 'manuais_sem_area', titulo: 'Receitas lançadas à mão sem área',
    onde: <span>Corrija em <IrPara para={LINK_LANCAMENTOS}>Financeiro › DRE & Resultado › Lançamentos</IrPara>.</span>,
  },
  {
    chave: 'contratos_sem_mix', titulo: 'Contratos com custo, mas sem nenhuma receita (o custo vai para “sem setor”)',
    onde: <span>Defina a divisão em <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara>.</span>,
  },
  {
    chave: 'contratos_mix_padrao', titulo: 'Contratos cuja divisão por setor não vem das parcelas (usamos o padrão, o faturamento antigo ou o rótulo)',
    onde: <span>Confira no contrato: Visão geral › cartão “Áreas e CNAE” (<IrPara para={LINK_AREAS}>lista</IrPara>).</span>,
  },
  {
    chave: 'custos_sem_setor', titulo: 'Custos sem setor',
    onde: <span>Pessoa: defina na <IrPara para="/administrativo/rh">ficha de RH</IrPara> (bloco Setores que a pessoa atende). Kit: <IrPara para="/operacional/kits">Operacional › Kits de equipamento</IrPara>. Lançamento e nota: revise na <IrPara para="/financeiro/dre-hub?aba=setores-custo">aba Setor dos custos</IrPara>. Sem setor, o custo segue a mistura do contrato.</span>,
  },
  {
    chave: 'custos_sem_casamento', titulo: 'Custo com setor que o contrato não tem',
    onde: <span>O setor do custo não aparece na receita do contrato, então o custo caiu na mistura do contrato. Ajuste o setor na <IrPara para="/administrativo/rh">ficha de RH</IrPara> (pessoa) ou em <IrPara para="/operacional/kits">Kits de equipamento</IrPara> (kit), ou a divisão do contrato em <IrPara para={LINK_AREAS}>Áreas e CNAE</IrPara>. Lançamento e nota: revise na <IrPara para="/financeiro/dre-hub?aba=setores-custo">aba Setor dos custos</IrPara>. Se está certo assim (ajuda pontual fora do setor da pessoa), use <strong>Aceitar: segue o contrato</strong> na linha, ou aceite o contrato inteiro — a Margem para de apontar depois do recálculo.</span>,
  },
];

const TIPO_ITEM: Record<string, string> = { pessoa: 'Pessoa', lancamento: 'Lançamento', bem: 'Kit/bem', nota: 'Nota' };
const num0 = (v: any) => Number(v) || 0;

/** Tabela de itens das pendências de setor do custo. */
function TabelaItensSetorCusto({ itens, podeAceitar, onAceitar }: { itens: any[]; podeAceitar: boolean; onAceitar: (it: any, escopo: EscopoAceite) => void }) {
  return (
    <Table
      size="small" pagination={false} rowKey={(_, i) => String(i)} scroll={{ x: 'max-content' }}
      dataSource={itens.slice(0, 15)}
      columns={[
        { title: 'Tipo', key: 'tipo', render: (_: any, it: any) => TIPO_ITEM[it.tipo] || it.tipo || '—' },
        {
          title: 'Nome', key: 'nome',
          render: (_: any, it: any) => (it.tipo === 'pessoa' && it.ref
            ? <IrPara para={`/administrativo/rh?pessoa=${encodeURIComponent(String(it.ref))}&aba=rateio`}>{it.nome || it.ref}</IrPara>
            : (it.nome || it.ref || '—')),
        },
        { title: 'Contrato', key: 'contrato', render: (_: any, it: any) => it.numero || it.contrato_id || '—' },
        { title: 'Linha', dataIndex: 'linha', key: 'linha', render: (v: string) => (v ? rotuloLinha(v) : '—') },
        { title: 'Setor do custo', key: 'item', render: (_: any, it: any) => nomesSetores(it.setores_item) },
        { title: 'Mistura do contrato', key: 'mix', render: (_: any, it: any) => nomesSetores(it.mix) },
        { title: 'Valor', key: 'valor', align: 'right' as const, render: (_: any, it: any) => reais(num0(it.valor)) },
        { title: 'Meses', key: 'meses', render: (_: any, it: any) => (Array.isArray(it.meses) ? it.meses.map(rotuloMesCurto).join(', ') : '—') },
        {
          title: 'Aceite ou revisão', key: 'aceite',
          render: (_: any, it: any) => {
            if (it.aceito) {
              return <Tag color="cyan">Aceito{it.aceito.escopo === 'contrato' ? ' (contrato inteiro)' : ''}{it.aceito.por ? ` por ${it.aceito.por}` : ''} — recalcule</Tag>;
            }
            if (it.tipo === 'lancamento' || it.tipo === 'nota') return <Text type="secondary">revise na aba Setor dos custos</Text>;
            if (!it.ref || !it.contrato_id) return null;
            return (
              <Space size={4} wrap>
                <Popconfirm
                  title={`Aceitar: no ${it.numero || 'contrato'}, o custo de ${it.nome || it.ref} que não casa com a receita segue a divisão do contrato?`}
                  okText="Aceitar" cancelText="Cancelar" onConfirm={() => onAceitar(it, it.tipo)} disabled={!podeAceitar}>
                  <Button size="small" type="primary" disabled={!podeAceitar}>Aceitar: segue o contrato</Button>
                </Popconfirm>
                <Popconfirm
                  title={`No ${it.numero || 'contrato'}, todo custo cujo setor o contrato não vende passa a seguir a divisão do contrato (o que já casa não muda; marcação manual vence)?`}
                  okText="Aceitar o contrato inteiro" cancelText="Cancelar" onConfirm={() => onAceitar(it, 'contrato')} disabled={!podeAceitar}>
                  <Button size="small" disabled={!podeAceitar}>todo o contrato</Button>
                </Popconfirm>
              </Space>
            );
          },
        },
      ]}
    />
  );
}

/** Os aceites registrados ("segue o contrato"), com Desfazer. */
function TabelaAceites({ aceites, podeAceitar, onDesfazer }: { aceites: any[]; podeAceitar: boolean; onDesfazer: (id: number) => void }) {
  const ESCOPO: Record<string, string> = { contrato: 'contrato inteiro', pessoa: 'pessoa', bem: 'equipamento', kit: 'kit' };
  return (
    <Table
      size="small" pagination={false} rowKey={(a: any) => String(a.id)} scroll={{ x: 'max-content' }}
      dataSource={aceites}
      columns={[
        { title: 'Contrato', key: 'contrato', render: (_: any, a: any) => a.numero || a.contrato_id },
        { title: 'O que segue o contrato', key: 'o_que', render: (_: any, a: any) => (a.escopo === 'contrato' ? 'Todo custo do contrato' : `${ESCOPO[a.escopo] || a.escopo}: ${a.nome || a.ref}`) },
        { title: 'Por', dataIndex: 'por', key: 'por' },
        { title: 'Em', key: 'em', render: (_: any, a: any) => (a.em ? dataBr(String(a.em).slice(0, 10)) : '—') },
        {
          title: '', key: 'acao',
          render: (_: any, a: any) => (
            <Popconfirm title="Desfazer este aceite? O custo volta a ser apontado depois do recálculo." okText="Desfazer" cancelText="Cancelar" onConfirm={() => onDesfazer(Number(a.id))} disabled={!podeAceitar}>
              <Button size="small" disabled={!podeAceitar}>Desfazer</Button>
            </Popconfirm>
          ),
        },
      ]}
    />
  );
}

const corNumero = (v: number) => (v < -0.005 ? '#a8071a' : undefined);

export default function MargemSetor({ podeVer = true, hoje: hojeProp }: PropsPagina) {
  const { pode, carregando: carregandoPerms, erro: erroPerms, recarregar } = usePerms();
  const { message } = App.useApp();
  const liberado = podeVer && pode(PERM);
  const podeAceitar = pode(PERM_REVISAR);
  const { hoje, filtro, setFiltro, janela } = usePeriodo(hojeProp, MAX_MESES_MARGEM);
  const de = janela.ok ? janela.de : null;
  const ate = janela.ok ? janela.ate : null;
  const [setor, setSetor] = useState<string | null>(null);
  /** null = automático: ligada com um setor escolhido, desligada com "Todos". */
  const [tendenciaManual, setTendenciaManual] = useState<boolean | null>(null);

  const [linha, setLinha] = useState<LinhaMargem>('lucro_liq');
  const [modo, setModo] = useState<ModoMargem>('mes');
  const [agrupar, setAgrupar] = useState<Agrupar>('frentes');
  const [dados, setDados] = useState<DreSetores | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [progresso, setProgresso] = useState<string | null>(null);
  const [erro, setErro] = useState<ErroDre | null>(null);
  const [recalculando, setRecalculando] = useState(false);
  const pedido = useRef(0);

  const carregar = useCallback(async () => {
    if (!de || !ate) return;
    const meu = ++pedido.current;
    setCarregando(true); setErro(null); setProgresso(null);
    try {
      let resp: DreSetores;
      try {
        resp = await buscarDreSetores({ de, ate, agrupar });
      } catch (e: any) {
        // Muitas DREs de uma vez podem passar do tempo da conexão: pede em blocos de 3 meses.
        const lento = e instanceof ErroDre && (e.status == null || e.status >= 500);
        if (!lento) throw e;
        const blocos = blocosDeMeses(de, ate, 3);
        const partes: DreSetores[] = [];
        for (let i = 0; i < blocos.length; i++) {
          if (meu !== pedido.current) return;
          setProgresso(`Calculando em partes (${i + 1} de ${blocos.length})...`);
          partes.push(await buscarDreSetores({ ...blocos[i], agrupar }));
        }
        resp = juntarBlocos(partes);
      }
      if (meu === pedido.current) setDados(resp);
    } catch (e: any) {
      if (meu === pedido.current) setErro(e instanceof ErroDre ? e : new ErroDre(String(e?.message || e), null));
    } finally {
      if (meu === pedido.current) { setCarregando(false); setProgresso(null); }
    }
  }, [agrupar, de, ate]);

  useEffect(() => { if (liberado) carregar(); }, [liberado, carregar]);

  const avisos = useMemo(() => (dados ? avisosDosMeses(dados.meses) : null), [dados]);
  const chaveRec = useMemo(() => (dados ? chaveReceita(dados.linhas) : 'receita_bruta'), [dados]);

  // Setores que aparecem: os que têm algum valor em alguma linha do período.
  const setores = useMemo(() => {
    if (!dados) return [];
    const mapas = dados.linhas.map((l) => valoresAcumulados(dados, l.chave));
    const todos = setoresDaResposta(dados.setores, mapas);
    return todos.filter((s) => mapas.some((m) => Math.abs(m[s] || 0) >= 0.5));
  }, [dados]);

  const grafico = useMemo(() => (dados ? linhasMargem(dados, linha, setores, modo) : []), [dados, linha, setores, modo]);
  // Setor que não existe no agrupamento atual (trocou 5 frentes ↔ 6 setores) vale "Todos".
  const setorEf = setor && setores.includes(setor) ? setor : null;
  const comTendencia = (tendenciaManual ?? setorEf != null) && modo === 'mes';
  const foco = useMemo(() => {
    const serie: MesSerie[] = grafico.map((g) => ({
      periodo: g.periodo, parcial: g.parcial, total: g.total,
      porSetor: Object.fromEntries(setores.map((s) => [s, Number(g[s]) || 0])),
    }));
    const r = linhasDoGrafico(serie, setores, setorEf, comTendencia);
    if (modo === 'acumulado') r.linhas.forEach((l) => { l.label = 'Período'; });
    return r;
  }, [grafico, setores, setorEf, comTendencia, modo]);

  // Com um setor escolhido: a DRE dele mês a mês (linhas da DRE × meses + o período).
  const linhasDoSetor = useMemo(() => {
    if (!dados || !setorEf) return [];
    const doMes = (m: { valores: Record<string, any> }, chave: string) => porSetorCanonico(m.valores?.[chave])[setorEf] || 0;
    const recMes = Object.fromEntries(dados.meses.map((m) => [m.periodo, doMes(m, chaveRec)]));
    const recPeriodo = valoresAcumulados(dados, chaveRec)[setorEf] || 0;
    const rows: any[] = [];
    dados.linhas.forEach((l) => {
      const v: Record<string, number> = Object.fromEntries(dados.meses.map((m) => [m.periodo, doMes(m, l.chave)]));
      v.periodo = valoresAcumulados(dados, l.chave)[setorEf] || 0;
      const ehLucro = LINHAS_DE_LUCRO.some((x) => x.value === l.chave);
      const destaque = ehLucro || /sub|total|resultado/.test(l.tipo || '') || l.chave === chaveRec;
      rows.push({ key: l.chave, rotulo: rotuloLinha(l.chave, l.rotulo), valores: v, destaque, margem: false });
      if (ehLucro) {
        const mg: Record<string, number | null> = { periodo: margemPct(v.periodo, recPeriodo) };
        dados.meses.forEach((m) => { mg[m.periodo] = margemPct(v[m.periodo], recMes[m.periodo]); });
        rows.push({ key: `${l.chave}_pct`, rotulo: `Margem do ${rotuloLinha(l.chave, l.rotulo).toLowerCase()} (sobre a receita bruta)`, valores: mg, destaque: false, margem: true });
      }
    });
    return rows;
  }, [dados, setorEf, chaveRec]);

  const linhasTabela = useMemo(() => {
    if (!dados) return [];
    const rec = valoresAcumulados(dados, chaveRec);
    const rows: any[] = [];
    dados.linhas.forEach((l) => {
      const v = valoresAcumulados(dados, l.chave);
      const destaque = LINHAS_DE_LUCRO.some((x) => x.value === l.chave) || /sub|total|resultado/.test(l.tipo || '') || l.chave === chaveRec;
      rows.push({ key: l.chave, rotulo: rotuloLinha(l.chave, l.rotulo), valores: v, destaque, margem: false });
      if (LINHAS_DE_LUCRO.some((x) => x.value === l.chave)) {
        const m: Record<string, number | null> = { total: margemPct(v.total || 0, rec.total || 0) };
        setores.forEach((s) => { m[s] = margemPct(v[s] || 0, rec[s] || 0); });
        rows.push({ key: `${l.chave}_pct`, rotulo: `Margem do ${rotuloLinha(l.chave, l.rotulo).toLowerCase()} (sobre a receita bruta)`, valores: m, destaque: false, margem: true });
      }
    });
    return rows;
  }, [dados, chaveRec, setores]);

  async function recalcular() {
    if (!avisos) return;
    setRecalculando(true);
    const falhas: string[] = [];
    // Até 6 por clique: cada mês roda a DRE geral inteira (alguns segundos).
    for (const p of avisos.recalculaveis.slice(0, 6)) {
      try { await recalcularFechamento(p); } catch (e: any) { falhas.push(`${rotuloMes(p)}: ${e.message}`); }
    }
    setRecalculando(false);
    if (falhas.length) message.error('Não calculou: ' + falhas.join(' · '));
    else message.success('Custos recalculados.');
    carregar();
  }

  if (carregandoPerms) return <Carregando />;
  if (!liberado) return podeVer && erroPerms ? <AcessoNaoConferido onTentar={recarregar} /> : <SemAcesso />;

  const mesesFechados = foco.linhas.filter((l) => !l.parcial).length;
  const controles = (
    <Space wrap size={[16, 8]} style={{ marginBottom: 12 }}>
      <FiltroDePeriodo filtro={filtro} onChange={setFiltro} hoje={hoje} janela={janela} />
      <FiltroDeSetor setores={setores} valor={setorEf} onChange={(s) => { setSetor(s); setTendenciaManual(null); }} />
      <FiltroDeTendencia
        ligado={comTendencia} onChange={setTendenciaManual}
        desligadoPorque={modo === 'acumulado' ? 'No acumulado o período vira uma barra só.' : dados && mesesFechados < 2 ? 'Precisa de pelo menos 2 meses fechados no período.' : null}
      />
      <Segmented value={linha} onChange={(v) => setLinha(v as LinhaMargem)} options={LINHAS_DE_LUCRO} />
      <Segmented value={modo} onChange={(v) => setModo(v as ModoMargem)} options={[{ value: 'mes', label: 'Mês a mês' }, { value: 'acumulado', label: 'Acumulado' }]} />
      <Segmented value={agrupar} onChange={(v) => setAgrupar(v as Agrupar)} options={[{ value: 'frentes', label: '5 frentes' }, { value: 'setores', label: '6 setores' }]} />
    </Space>
  );
  const avisoJanela = janela.ok
    ? (janela.aviso ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message={janela.aviso} /> : null)
    : <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={janela.aviso} />;

  if (!janela.ok) return <div>{controles}{avisoJanela}</div>;
  if (erro && !dados) {
    return (
      <div>
        {controles}{avisoJanela}
        {erro.status === 403 ? <SemAcesso /> : (
          <Alert type="error" showIcon message="Não foi possível calcular a margem por setor" description={erro.message}
            action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
        )}
      </div>
    );
  }
  if (!dados || !avisos) return <div>{controles}{avisoJanela}<Carregando texto={progresso || 'Calculando a DRE de cada mês, pode levar alguns segundos...'} /></div>;

  const semSetor = totalSemSetor(dados.meses, chaveRec);
  const maoObra = totalMaoObraParada(dados.meses);
  const calculadoEm = dataBr(calculadoEmMaisAntigo(dados.meses));
  const lista = (ps: string[]) => ps.map(rotuloMes).join(', ');
  const rotuloLinhaAtual = LINHAS_DE_LUCRO.find((x) => x.value === linha)?.label || linha;
  const pendenciasComItem = PENDENCIAS.filter((p) => contagemPendencia(dados.pendencias?.[p.chave]) > 0);
  const aceitesRegistrados: any[] = Array.isArray((dados.pendencias as any)?.aceites) ? (dados.pendencias as any).aceites : [];
  const aceitar = async (it: any, escopo: EscopoAceite) => {
    try {
      const r = await aceitarSetorCusto({ contrato_id: String(it.contrato_id), escopo, ref: escopo === 'contrato' ? null : String(it.ref) });
      message.success(r.ja_existia ? 'Esse aceite já existia.' : 'Aceite registrado. Recalcule os meses do contrato para a lista atualizar.', 6);
      await carregar();
    } catch (e: any) { message.error(e.message || 'Não consegui registrar o aceite.'); }
  };
  const desfazerAceite = async (id: number) => {
    try {
      await desfazerAceiteSetorCusto(id);
      message.success('Aceite desfeito. Recalcule os meses do contrato.', 6);
      await carregar();
    } catch (e: any) { message.error(e.message || 'Não consegui desfazer.'); }
  };

  const colunas = [
    { title: 'Linha da DRE', dataIndex: 'rotulo', key: 'rotulo', fixed: 'left' as const, width: 260 },
    ...setores.map((s) => ({
      title: <EtiquetaSetor chave={s} />, key: s, align: 'right' as const,
      render: (_: any, r: any) => {
        const v = r.valores[s];
        if (v == null) return r.margem ? '—' : '';
        return <span style={{ color: corNumero(v) }}>{r.margem ? pctTexto(v) : reais(v)}</span>;
      },
    })),
    {
      title: 'Total (= DRE)', key: 'total', align: 'right' as const,
      render: (_: any, r: any) => {
        const v = r.valores.total;
        return <Text strong style={{ color: corNumero(v || 0) }}>{r.margem ? pctTexto(v) : reais(v || 0)}</Text>;
      },
    },
  ];

  const rotuloSetor = setorEf ? metaSetor(setorEf).rotulo : null;
  const tendencia = foco.tendencia;
  const nSeries = (setorEf ? 1 : setores.length + 1) + (tendencia ? 1 : 0);
  const colunasDoSetor = [
    { title: 'Linha da DRE', dataIndex: 'rotulo', key: 'rotulo', fixed: 'left' as const, width: 260 },
    ...dados.meses.map((m) => ({
      title: rotuloMesCurto(m.periodo) + (m.parcial ? '*' : ''), key: m.periodo, align: 'right' as const,
      render: (_: any, r: any) => {
        const v = r.valores[m.periodo];
        if (v == null) return r.margem ? '—' : '';
        return <span style={{ color: corNumero(v) }}>{r.margem ? pctTexto(v) : reais(v)}</span>;
      },
    })),
    {
      title: 'Período', key: 'periodo', align: 'right' as const,
      render: (_: any, r: any) => {
        const v = r.valores.periodo;
        if (v == null) return r.margem ? '—' : '';
        return <Text strong style={{ color: corNumero(v) }}>{r.margem ? pctTexto(v) : reais(v)}</Text>;
      },
    },
  ];

  return (
    <div>
      {controles}
      {avisoJanela}

      <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
        <Text type="secondary">
          {rotuloMes(dados.de)} a {rotuloMes(dados.ate)}. Custos de contrato vão para o setor pela receita do contrato na vida toda; o resto (administrativo, comercial, financeiro, impostos) é dividido pela receita do setor no mês.
          {calculadoEm ? ` Divisão dos custos calculada em ${calculadoEm}.` : ''}
        </Text>
        {avisos.conferenciaErrada.length ? (
          <Alert
            type="error" showIcon
            message="A soma dos setores não bate com a DRE"
            description={avisos.conferenciaErrada.map((c) => `${rotuloMes(c.periodo)}: diferença de ${reais(c.diferenca)}`).join(' · ')}
          />
        ) : null}
        {avisos.recalculaveis.length ? (
          <Alert
            type="warning" showIcon
            message={`Custos de ${lista(avisos.recalculaveis)} estão desatualizados`}
            description="A soma continua fechando com a DRE, mas a divisão dos custos por contrato foi calculada antes da regra de hoje (ou ainda não foi calculada)."
            action={<Button size="small" icon={<ReloadOutlined />} loading={recalculando} onClick={recalcular}>Recalcular</Button>}
          />
        ) : null}
        {avisos.setoresDesatualizados.length ? (
          <Text type="secondary">{rotuloRegra('setores_desatualizados')}: {lista(avisos.setoresDesatualizados)}.</Text>
        ) : null}
        {resumoVias(somarVias(dados.meses.map((m) => m.vias))) ? (
          <Text type="secondary">
            Custo direto no período: {resumoVias(somarVias(dados.meses.map((m) => m.vias)))}.
          </Text>
        ) : null}
        {avisos.congeladas.length || avisos.antigas.some((p) => !avisos.recalculaveis.includes(p)) ? (
          <Alert
            type="info" showIcon
            message={`Meses antes de 2026 usam a regra antiga de mão de obra: ${lista([...avisos.congeladas, ...avisos.antigas.filter((p) => !avisos.congeladas.includes(p) && !avisos.recalculaveis.includes(p))])}`}
            description="Esses meses estão congelados e não podem ser recalculados. A margem deles só vale como ordem de grandeza."
          />
        ) : null}
        {avisos.abertos.length ? (
          <Alert
            type="info" showIcon
            message={`${lista(avisos.abertos)} ainda está em andamento`}
            description="A despesa já entra no começo do mês e a receita só quando a nota é emitida, então o lucro deste mês aparece menor do que será."
          />
        ) : null}
        {Math.abs(semSetor) >= 0.01 ? (
          <Alert
            type="warning" showIcon
            message={`${reais(semSetor)} de receita sem setor`}
            description={<span>Aparece na coluna “Sem setor”. Para corrigir: <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara>.</span>}
          />
        ) : null}
        {maoObra >= 0.01 ? (
          <Alert
            type="info" showIcon
            message={`${reais(maoObra)} de mão de obra parada foi dividida entre os setores`}
            description={<span>É o pagamento de quem não anotou horas ou anotou em destino sem contrato; foi repartido pela receita de cada setor. Para conferir: <IrPara para={LINK_MAO_OBRA}>Financeiro › DRE & Resultado › Mão de obra</IrPara>.</span>}
          />
        ) : null}
        {erro ? <Alert type="error" showIcon message="Não consegui atualizar" description={erro.message} /> : null}
      </Space>

      <div style={{ opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        <Text strong>{rotuloSetor ? `${rotuloLinhaAtual} de ${rotuloSetor}` : `${rotuloLinhaAtual} por setor`}</Text>
        <Text type="secondary">{rotuloSetor ? ' (prejuízo desce abaixo do zero)' : ' (prejuízo desce abaixo do zero; a linha cheia é o total da empresa, igual à DRE)'}</Text>
        <div style={{ height: 'clamp(300px, calc(100vh - 420px), 520px)', marginTop: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={foco.linhas} stackOffset="sign" margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={reaisCurto} width={56} />
              <Tooltip content={(p: any) => (
                <TooltipEmpilhado active={p.active} payload={p.payload} label={p.label} rotuloTotal="Total da empresa" umaBarra={setorEf != null} />
              )} />
              {nSeries >= 2 ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
              <ReferenceLine y={0} stroke="#8c8c8c" />
              {(setorEf ? [setorEf] : setores).map((s) => {
                const m = metaSetor(s);
                return <Bar key={s} dataKey={s} name={m.rotulo} stackId="lucro" fill={m.cor} stroke="#fff" strokeWidth={2} maxBarSize={56} />;
              })}
              {setorEf ? null : <Line type="monotone" dataKey="total" name="Total da empresa" stroke="#262626" strokeWidth={2} dot={{ r: 3 }} />}
              {tendencia ? (
                <Line
                  type="linear" dataKey="tendencia" name={`Tendência (${tendenciaTexto(tendencia.porMes)})`}
                  stroke={COR_TENDENCIA} strokeDasharray={TRACO_TENDENCIA} strokeWidth={2} dot={false} activeDot={false}
                  legendType="plainline" isAnimationActive={false}
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {avisos.abertos.length && modo === 'mes' ? <Text type="secondary">* mês em andamento.</Text> : null}
      </div>

      <div style={{ marginTop: 24, opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        {setorEf ? (
          <>
            <Text strong>DRE de {rotuloSetor}, mês a mês</Text>
            <Text type="secondary"> (o período é a soma das DREs de cada mês)</Text>
          </>
        ) : (
          <>
            <Text strong>DRE por setor, no período todo</Text>
            <Text type="secondary"> (o total é a soma das DREs de cada mês; o imposto estimado é calculado mês a mês, então pode diferir um pouco da DRE trimestral)</Text>
          </>
        )}
        <Table
          style={{ marginTop: 8 }} size="small" pagination={false} scroll={{ x: 'max-content' }}
          columns={setorEf ? colunasDoSetor : colunas} dataSource={setorEf ? linhasDoSetor : linhasTabela}
          onRow={(r) => ({ style: r.destaque ? { fontWeight: 600, background: '#fafafa' } : r.margem ? { fontStyle: 'italic', color: '#595959' } : undefined })}
        />
      </div>

      {(pendenciasComItem.length || aceitesRegistrados.length) ? (
        <div style={{ marginTop: 24 }}>
          <Text strong>O que conferir</Text>
          <Collapse
            style={{ marginTop: 8 }}
            items={[...pendenciasComItem.map((p) => {
              const itens = itensDaPendencia(dados.pendencias[p.chave]);
              const n = contagemPendencia(dados.pendencias[p.chave]);
              return {
                key: p.chave,
                label: `${p.titulo} (${n})`,
                children: (
                  <div>
                    <div style={{ marginBottom: 8 }}>{p.onde}</div>
                    {itens.length && (p.chave === 'custos_sem_setor' || p.chave === 'custos_sem_casamento') ? (
                      <TabelaItensSetorCusto itens={itens} podeAceitar={podeAceitar} onAceitar={aceitar} />
                    ) : itens.length ? (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {itens.slice(0, 15).map((it, i) => <li key={i}>{rotuloItem(it)}</li>)}
                      </ul>
                    ) : null}
                    {n > 15 ? <Text type="secondary">e mais {n - 15}.</Text> : null}
                  </div>
                ),
              };
            }), ...(aceitesRegistrados.length ? [{
              key: 'aceites',
              label: `Aceites registrados: custos que seguem o contrato por decisão sua (${aceitesRegistrados.length})`,
              children: <TabelaAceites aceites={aceitesRegistrados} podeAceitar={podeAceitar} onDesfazer={desfazerAceite} />,
            }] : [])]}
          />
        </div>
      ) : null}
    </div>
  );
}
